import { Link } from "react-router-dom";
import { ArrowLeft, TrendingDown, ArrowRight } from "lucide-react";
import CheckinComparison from "@/components/session/CheckinComparison";
import { useDemoState } from "../demo/DemoStateProvider";

/**
 * CheckinHistory (Scope A2) — the canned pre→post check-in history, rendered with
 * the REAL CheckinComparison leaf (valence-grouped emotions + scales). Pure demo
 * data from the in-memory store; nothing is fetched.
 */
export function CheckinHistory() {
  const { sessions, clients } = useDemoState();
  const nameFor = (id: string) => clients.find((c) => c.id === id)?.displayName ?? "Sample client";

  return (
    <main className="mx-auto max-w-4xl px-4 py-8">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-medium text-[var(--color-text-primary)]">Check-in history</h1>
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-ghost)] transition-colors hover:text-[var(--color-gold-soft)]">
          <ArrowLeft className="h-3 w-3" /> Dashboard
        </Link>
      </div>
      <p className="mt-1 text-[14px] text-[var(--color-text-muted)]">
        Pre- and post-session check-ins compound over time. This is how a therapist sees the change a session made.
      </p>

      <div className="mt-6 space-y-4">
        {sessions.map((ck) => {
          // Only a real pre AND post reading yields a distress-change badge (null = skipped scale).
          const delta = ck.pre.distress != null && ck.post.distress != null ? ck.pre.distress - ck.post.distress : 0;
          return (
            <article key={ck.id} className="overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-navy-mid)]">
              <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-border)] px-5 py-3">
                <div>
                  <p className="font-heading text-[16px] font-medium text-[var(--color-text-primary)]">{ck.topic}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-text-ghost)]">
                    {nameFor(ck.clientId)} · {ck.date}
                  </p>
                </div>
                {delta > 0 && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border-gold)] px-3 py-1 font-mono text-[10px] uppercase tracking-wide text-[var(--color-gold-soft)]">
                    <TrendingDown className="h-3.5 w-3.5" /> Distress {ck.pre.distress} → {ck.post.distress} (−{delta})
                  </span>
                )}
              </header>
              <div className="grid gap-5 p-5 sm:grid-cols-[1fr_auto_1fr] sm:items-start">
                <CheckinComparison
                  title="At the start"
                  emotions={ck.pre.emotions}
                  distress={ck.pre.distress}
                  capability={ck.pre.capability}
                  goal={ck.pre.goal}
                />
                <div className="hidden items-center justify-center pt-10 text-[var(--color-gold-soft)] sm:flex">
                  <ArrowRight className="h-5 w-5" />
                </div>
                <CheckinComparison
                  title="At the end"
                  emotions={ck.post.emotions}
                  distress={ck.post.distress}
                  capability={ck.post.capability}
                  note={ck.post.note}
                />
              </div>
            </article>
          );
        })}
      </div>
    </main>
  );
}
